import { Theme } from "@mui/material/styles";
import { Dayjs } from "dayjs";
import { formatDate } from "../../../../../../views/components/common/functions/formatDate";
import { AlertsSummaryCard } from "../../../../../../models/Dashboard/AlertsDashboard/AlertsDashboardSummary";

export const ALERTS_EMPTY_VALUE = "—";

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

export const formatAlertsCount = (value: number | null | undefined): string => {
  try {
    if (!isFiniteNumber(value)) {
      return ALERTS_EMPTY_VALUE;
    }
    return value.toLocaleString("en-IN");
  } catch (error) {
    return ALERTS_EMPTY_VALUE;
  }
};

/** Percent values already carry one decimal from the server; only append `%`. */
export const formatAlertsPct = (value: number | null | undefined): string => {
  try {
    if (!isFiniteNumber(value)) {
      return ALERTS_EMPTY_VALUE;
    }
    return `${value}%`;
  } catch (error) {
    return ALERTS_EMPTY_VALUE;
  }
};

/** Card value: count, or percent ("—" when a read-rate card has no inbox rows). */
export const formatAlertsCardValue = (card: AlertsSummaryCard): string => {
  try {
    if (card.unit === "pct") {
      if (card.denominator === 0) {
        return ALERTS_EMPTY_VALUE;
      }
      return formatAlertsPct(card.value);
    }
    return formatAlertsCount(card.value);
  } catch (error) {
    return ALERTS_EMPTY_VALUE;
  }
};

/** UTC `YYYY-MM-DD HH:mm:ss` → local display. */
export const formatAlertsDateTime = (value: unknown): string => {
  try {
    if (value == null || value === "") {
      return ALERTS_EMPTY_VALUE;
    }
    const formatted = formatDate(value, true);
    return formatted && formatted !== "-" ? formatted : String(value);
  } catch (error) {
    return ALERTS_EMPTY_VALUE;
  }
};

export const formatAlertsStat = (
  value: number | string | null | undefined,
): string => {
  try {
    if (value == null || value === "") {
      return ALERTS_EMPTY_VALUE;
    }
    if (typeof value === "number") {
      return formatAlertsCount(value);
    }
    return value;
  } catch (error) {
    return ALERTS_EMPTY_VALUE;
  }
};

export const formatAlertsText = (value: unknown): string => {
  try {
    if (value == null || value === "") {
      return ALERTS_EMPTY_VALUE;
    }
    if (typeof value === "number") {
      return formatAlertsCount(value);
    }
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    return String(value);
  } catch (error) {
    return ALERTS_EMPTY_VALUE;
  }
};

/** `CLIENT_INVENTORY_LOW_STOCK` → `Client inventory low stock`. */
export const humanizeAlertsCode = (code: string | null | undefined): string => {
  try {
    if (!code) {
      return ALERTS_EMPTY_VALUE;
    }
    const words = code.toLowerCase().split("_").filter(Boolean).join(" ");
    return words.charAt(0).toUpperCase() + words.slice(1);
  } catch (error) {
    return code || ALERTS_EMPTY_VALUE;
  }
};

export const alertsRangeSlug = (start: Dayjs, end: Dayjs): string => {
  try {
    return `${start.format("YYYY-MM-DD")}_to_${end.format("YYYY-MM-DD")}`;
  } catch (error) {
    return "range";
  }
};

export interface AlertsTone {
  fg: string;
  bg: string;
}

/**
 * Tone for any status-like value on this dashboard (alert status, severity,
 * push / email / delivery / log states). Colours come from ThemeContext only.
 */
export const alertsTone = (theme: Theme, raw: unknown): AlertsTone => {
  const colors = theme.palette.customColors;
  const ok = {
    fg: colors.successGreen,
    bg: colors.successGreenLightBackground,
  };
  const warn = { fg: theme.palette.goldenYellow.main, bg: colors.lightOrange };
  const bad = { fg: colors.errorRed, bg: theme.palette.blushPink.main };
  const info = { fg: colors.darkBlue, bg: colors.blueLightBackground };
  const mute = { fg: colors.greyTextColor, bg: colors.veryLightGray };

  const value = String(raw ?? "").toUpperCase();
  switch (value) {
    case "RESOLVED":
    case "SENT":
    case "DELIVERED":
    case "PROCESSED":
      return ok;
    case "LOW":
      return info;
    case "OPEN":
    case "PENDING":
    case "QUEUED":
    case "RETRYABLE":
    case "DEFERRED":
    case "MEDIUM":
      return warn;
    case "FAILED":
    case "BOUNCED":
    case "BOUNCE":
    case "DROPPED":
    case "SEND_FAILED":
    case "INVALID_TOKEN":
    case "ERROR":
    case "HIGH":
      return bad;
    case "ADMIN":
    case "CUSTOMER":
    case "CUSTOMER_USER":
      return info;
    default:
      return mute;
  }
};

/** Columns whose values render as a tone pill. */
export const ALERTS_PILL_KEYS = new Set([
  "status",
  "severity",
  "audience",
  "push_status",
  "email_status",
  "alert_status",
  "delivery_status",
]);

/** IDs stay plain digits — no locale commas. */
export const ALERTS_RAW_ID_KEYS = new Set([
  "alert_id",
  "batch_id",
  "delivery_id",
  "customer_id",
  "user_id",
  "admin_id",
  "group_id",
  "email_batch_id",
  "id",
]);
